/**
 * Backend API client. Wraps fetch with a Clerk session bearer token so the
 * Workers' requireClerkAuth middleware accepts the call.
 *
 * Use `useApi()` inside React components — it reads from Clerk's React
 * context. For plain calls outside React, prefer adding endpoints via the
 * extension's api-client.js instead.
 */

import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://api.grouppulsehq.com';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.status = status;
    this.body = body;
  }
}

export function useApi() {
  const { getToken } = useAuth();

  // Memoize so the returned `request` keeps a stable identity across renders.
  // Without this, calling components that depend on `request` in useEffect/
  // useCallback deps would refetch on every render.
  const request = useCallback(
    async <T>(path: string, init: RequestInit = {}): Promise<T> => {
      const token = await getToken();
      if (!token) throw new ApiError(401, null, 'no Clerk session');

      const headers = new Headers(init.headers);
      headers.set('Authorization', `Bearer ${token}`);
      if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }

      const res = await fetch(API_BASE_URL + path, { ...init, headers });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // empty body is fine
      }
      if (!res.ok) {
        throw new ApiError(res.status, body, `API ${res.status} ${path}`);
      }
      return body as T;
    },
    [getToken],
  );

  return { request };
}

// -- Typed payloads ----------------------------------------------------------

export type MeResponse =
  | {
      user_not_linked: true;
      clerk: { user_id: string; email: string | null; is_owner_email: boolean };
    }
  | {
      user_not_linked: false;
      user: {
        id: string;
        email: string;
        role: 'owner' | 'va' | 'va_admin' | 'client';
        agency_id: string | null;
        client_account_id: string | null;
        timezone: string | null;
      };
      counts: {
        total: number;
        buyer_intent: number;
        new_leads: number;
        won_leads: number;
      };
      /** Per-client feature flags. Always present (possibly empty). Only
       *  meaningful when role === 'client' — for owner/VA roles this is
       *  always `{}` and you should check flags per-client via the admin
       *  client-detail page instead. */
      feature_flags: Record<string, boolean>;
    };

export type LeadRow = {
  id: string;
  client_account_id: string;
  /** Friendly client name from a LEFT JOIN at /api/me/leads — null if the
   *  lead's client row was deleted. Surfaced in the Leads page header so
   *  owner/va_admin viewing leads across multiple clients can tell at a
   *  glance which client each lead belongs to. */
  client_account_name?: string | null;
  profile_id: string;
  fingerprint: string;
  detected_at: number;
  captured_via: string | null;
  group_id: string | null;
  group_name: string | null;
  group_url: string | null;
  post_text: string;
  post_url: string | null;
  author_name: string | null;
  author_url: string | null;
  likes: number | null;
  comments_count: number | null;
  intent: string | null;
  confidence: number | null;
  summary: string | null;
  /** 1 once AI intent classification has run on this lead, else 0. */
  analyzed: number;
  status: string | null;
  notes: string | null;
  outreached: number;
  saved: number;
  last_modified: number;
  /** JSON-encoded `string[]` of keywords matched when the lead was captured. */
  keywords_json: string | null;
  /** 'facebook' | 'reddit' — defaults to 'facebook' for legacy rows. */
  platform?: string | null;
  /** 1 = archived (hidden from the default Leads list), 0 = active. */
  archived: number;
  /** Unix-ms the lead was archived; null while active. */
  archived_at?: number | null;
  /** JSON-encoded `{ value_add, promotional }` once AI replies have been
   *  drafted (manually or by the auto-draft runner). null = not drafted. */
  replies_json?: string | null;
};

export type LeadsResponse = {
  leads: LeadRow[];
  next_cursor: number | null;
};

// GET /api/me/config — the signed-in user's editable monitoring config.
export type ConfigProfile = {
  id: string;
  name: string;
  industry: string | null;
  value_proposition: string | null;
  promotion: string | null;
  tone: string | null;
  match_mode: string | null;
  ai_training_json: string | null;
  created_at: number;
  updated_at: number;
};
export type ConfigKeyword = {
  id: string;
  profile_id: string;
  keyword: string;
  kind: 'positive' | 'negative';
  created_at: number;
};
export type ConfigWatchedGroup = {
  id: string;
  group_id: string;
  group_url: string | null;
  group_name: string | null;
  enabled: number;
  platform: string | null;
  created_at: number;
  last_swept_at: number | null;
};
export type MeConfigResponse = {
  client_account_id: string;
  profiles: ConfigProfile[];
  keywords: ConfigKeyword[];
  watched_groups: ConfigWatchedGroup[];
};

// GET /api/me/dashboard — birds-eye stats for the web dashboard.
export type DashboardResponse = {
  trend: {
    date: string; // YYYY-MM-DD
    facebook: number;
    reddit: number;
    total: number;
  }[];
  communities: {
    group_name: string;
    group_url: string | null;
    platform: string | null;
    lead_count: number;
    buyer_intent_count: number;
  }[];
  headline: {
    total: number;
    buyer_intent: number;
    new_leads: number;
    won_leads: number;
    last_7d: number;
    analyzed: number;
  };
};
